import React, { Fragment, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPowerOff } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { fetchSettings } from "./redux/usersSagas";
import { usePopup } from "./hooks/usePopup";
import { Trash, Edit as EditIcon, Email } from "../../../../assets/images/svg";
import LoadingAnimation from "../../../components/loadingAnimation/loadingAnimation";
import { Add } from "./smartComponents/Add";
import { Edit } from "./smartComponents/Edit";
import { Delete } from "./smartComponents/Delete";
import FilterUsers from "./smartComponents/FilterUsers";
import { Duplicate } from "./smartComponents/Duplicate";
import { Mirror } from "./smartComponents/Mirror";
import { LoginAs } from "./smartComponents/LoginAs";
import { AddAdmin } from "./smartComponents/AddAdmin";
import { Upload } from "./smartComponents/Upload";
import { Reactivate } from "./smartComponents/Reactivate";
import { EmailsTable } from "./smartComponents/EmailsTable";
import { SetAdmin } from "./smartComponents/SetAdmin";
import { Tags, TagsList } from "./smartComponents/Tags";
import { UsersSettingsMenu } from "./smartComponents/UsersSettingsMenu";
import { UserActionsMenu } from "./smartComponents/UserActionsMenu";
import { exportActiveUsers, makeRequest, menuActions } from "./api";
import "./users.scss";

export function UsersModule({ params }) {
  const dispatch = useDispatch();

  const users = useSelector((state) => state.users.users);

  const isLoading = useSelector((state) => state.users.isLoading);
  const areUsersLoading = useSelector((state) => state.users.areUsersLoading);

  const [selectedUser, setSelectedUser] = useState("");

  const [userEmailId, setUserEmailId] = useState(null);

  const { popup, openPopup, closePopup } = usePopup();

  const onMenuItemClick = ({ value, syntheticEvent }, item = {}) => {
    syntheticEvent.preventDefault();

    let { type, endpoint, requiresUser } = menuActions[value];

    if (type === "request") {
      if (value === "exportActiveUsers") {
        exportActiveUsers();
        return;
      }
      makeRequest(endpoint, item.id);
    }

    if (type === "popup") {
      if (requiresUser) {
        setSelectedUser(item);
      }
      openPopup(value);
    }
  };

  const handlePopup = (item, type) => {
    setSelectedUser(item);

    openPopup(type);
  };

  const handleEmails = (id) => {
    setUserEmailId(id === userEmailId ? null : id);
  };

  useEffect(() => {
    dispatch(fetchSettings(params));
  }, []);

  if (isLoading)
    return (
      <div className="light">
        <LoadingAnimation uiLoader />
      </div>
    );

  return (
    <div id="admin-users">
      <div className="admin-users">
        <div className="admin-users__menu">
          <UsersSettingsMenu onMenuItemClick={onMenuItemClick} />
        </div>

        <div className="admin-users__top">
          <FilterUsers />
        </div>

        <TagsList />

        <div className="admin-users__content">
          <ul className="group">
            {areUsersLoading ? (
              <LoadingAnimation />
            ) : (
              users &&
              Object.keys(users).map((orgName) => {
                return (
                  <Fragment key={orgName}>
                    <div style={{ fontWeight: "bold", marginBottom: 10, fontSize: "22px" }}>
                      {orgName}
                    </div>
                    {users[orgName].map((user) => {
                      const {
                        id,
                        firstName,
                        lastName,
                        statuses,
                        userActions: {
                          canShowEmailSends,
                          canReactivate,
                          canDeleteCompletely,
                          canDelete
                        }
                      } = user;

                      const userProps = { ...user, name: lastName + ", " + firstName, orgName };

                      return (
                        <>
                          <li color="dark" className="group__item" key={id}>
                            <div className="group__el text">
                              {lastName}, {firstName}
                            </div>

                            <div className="group__el icons">
                              <div className="group__el tags">
                                <Tags statuses={statuses} />
                              </div>
                              <div className="group__el admin-users__menu admin-users__menu--transparent">
                                <UserActionsMenu
                                  onMenuItemClick={onMenuItemClick}
                                  item={userProps}
                                />
                              </div>
                              {canShowEmailSends && (
                                <div className="group__el email">
                                  <span
                                    className="group__icon"
                                    onClick={() => handleEmails(user.id)}
                                    title="Show Sent Emails"
                                  >
                                    <Email />
                                  </span>
                                </div>
                              )}

                              <div className="group__el edit">
                                <span
                                  className="group__icon"
                                  onClick={() => handlePopup(userProps, "edit")}
                                  title="Edit"
                                >
                                  <EditIcon />
                                </span>
                              </div>

                              {canReactivate && (
                                <div className="group__el">
                                  <span
                                    className="group__icon"
                                    onClick={() => handlePopup(userProps, "reactivate")}
                                    title="Reactivate"
                                  >
                                    <FontAwesomeIcon icon={faPowerOff} color="red" />
                                  </span>
                                </div>
                              )}

                              {(canDelete || canDeleteCompletely) && (
                                <div className="group__el delete">
                                  <span
                                    className="group__icon"
                                    onClick={() => handlePopup(userProps, "delete")}
                                    title={`Delete ${statuses.isDeleted ? "Completely" : ""}`}
                                  >
                                    <Trash color="#FF0000" />
                                  </span>
                                </div>
                              )}
                            </div>
                          </li>
                          {id === userEmailId ? <EmailsTable id={id} /> : null}
                        </>
                      );
                    })}
                  </Fragment>
                );
              })
            )}

            {users && users.length === 0 && <p className="error error--medium">No users found</p>}
          </ul>
        </div>

        {popup.addUser && <Add onClose={closePopup("addUser")} />}
        {popup.addAdminUser && <AddAdmin onClose={closePopup("addAdminUser")} />}
        {popup.bulkUsersCreation && <Upload onClose={closePopup("bulkUsersCreation")} />}

        {popup.edit && (
          <Edit isOpen={popup.edit} onClose={closePopup("edit")} selected={selectedUser} />
        )}

        {popup.setAdmin && (
          <SetAdmin
            isOpen={popup.setAdmin}
            onClose={closePopup("setAdmin")}
            selected={{
              id: selectedUser.id,
              name: selectedUser.name,
              orgName: selectedUser.orgName
            }}
          />
        )}

        {popup.duplicate && (
          <Duplicate
            isOpen={popup.duplicate}
            onClose={closePopup("duplicate")}
            selected={{
              id: selectedUser.id,
              name: selectedUser.name,
              orgName: selectedUser.orgName
            }}
          />
        )}

        {popup.mirror && (
          <Mirror
            isOpen={popup.mirror}
            onClose={closePopup("mirror")}
            selected={{
              id: selectedUser.id,
              name: selectedUser.name,
              organizationId: selectedUser.organizationId,
              orgName: selectedUser.orgName
            }}
          />
        )}

        {popup.delete && (
          <Delete
            isOpen={popup.delete}
            onClose={closePopup("delete")}
            selected={{
              id: selectedUser.id,
              name: selectedUser.name,
              isDeleted: selectedUser.statuses.isDeleted
            }}
          />
        )}

        {popup.reactivate && (
          <Reactivate
            isOpen={popup.reactivate}
            onClose={closePopup("reactivate")}
            selected={{
              id: selectedUser.id,
              name: selectedUser.name
            }}
          />
        )}

        {popup.loginAs && (
          <LoginAs
            isOpen={popup.loginAs}
            onClose={closePopup("loginAs")}
            selected={{ id: selectedUser.id }}
          />
        )}
      </div>
    </div>
  );
}
